/* custom.js — site-wide behaviour (loaded deferred on every template).
   Product-template-only code (gallery drift controller, lightbox, sold-out overlay /
   waitlist modal, sticky variant picker, GoKwik button poll, review modal) lives in
   assets/notice-pdp.js, which theme.liquid loads right after this file on product
   templates only. Keep this file free of product-page selectors. */

function clearStaleDrawerBodyClasses() {
  if (document.querySelector('details#Details-menu-drawer-container[open]')) return;
  document.body.classList.remove('overflow-hidden-tablet', 'overflow-hidden-mobile', 'overflow-hidden-desktop');
}
document.addEventListener('DOMContentLoaded', clearStaleDrawerBodyClasses);
window.addEventListener('pageshow', clearStaleDrawerBodyClasses);

// aria-expanded sync shim: Dawn's MenuDrawer.closeMenuDrawer only resets
// aria-expanded="false" on KeyboardEvent (Escape). Click-close leaves it "true".
// DetailsModal.close never resets it. Patch both to sync unconditionally so CSS
// rules keyed off [aria-expanded] don't desync from actual drawer/modal state.
(function ariaExpandedSyncShim() {
  function patch(tagName, closeMethod, summaryGetter) {
    const Ctor = customElements.get(tagName);
    if (typeof Ctor !== 'function') return;
    const proto = Ctor.prototype;
    if (!proto || typeof proto[closeMethod] !== 'function' || proto[closeMethod]._ariaShimmed) return;
    const original = proto[closeMethod];
    proto[closeMethod] = function patched() {
      const result = original.apply(this, arguments);
      try {
        const summary = summaryGetter(this);
        if (summary) summary.setAttribute('aria-expanded', 'false');
      } catch (e) {}
      return result;
    };
    proto[closeMethod]._ariaShimmed = true;
  }
  function run() {
    patch('menu-drawer', 'closeMenuDrawer', inst => inst.mainDetailsToggle && inst.mainDetailsToggle.querySelector('summary'));
    patch('header-drawer', 'closeMenuDrawer', inst => inst.mainDetailsToggle && inst.mainDetailsToggle.querySelector('summary'));
    patch('details-modal', 'close', inst => inst.querySelector('summary'));
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();

// Mobile submenu fix: Dawn's MenuDrawer.onFocusOut closes the whole drawer
// when activeElement leaves mainDetailsToggle. On touch/mouse tap of a nested
// <summary> (e.g. SHOP ALL), activeElement briefly leaves the drawer, so the
// focusout handler fires and calls closeMenuDrawer() before the submenu can
// open. User sees drawer disappear.
// Listener is attached in the constructor via `.bind(this)`, so patching the
// prototype doesn't affect existing bindings. Instead: capture-phase listener
// on each mainDetailsToggle that stopImmediatePropagation prevents the bubble
// focusout handler from running. Escape key + outside-click + explicit close
// button paths all unaffected.
(function blockFocusOutCloseShim() {
  function attach(root) {
    const drawers = root.querySelectorAll('menu-drawer, header-drawer');
    for (const drawer of drawers) {
      const main = drawer.querySelector('details');
      if (!main || main._focusOutBlocked) continue;
      main.addEventListener('focusout', function(e) { e.stopImmediatePropagation(); }, true);
      main._focusOutBlocked = true;
    }
  }
  function run() { attach(document); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();

let _footerInViewObserver = null;

function footerInView() {
  const target = document.querySelector('.shopify-section-group-footer-group');

  if (!target) return;
  if (_footerInViewObserver) _footerInViewObserver.disconnect();

  _footerInViewObserver = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        document.body.classList.add('footer-visible');
      } else {
        document.body.classList.remove('footer-visible');
      }
    },
    { threshold: 0 }
  );

  _footerInViewObserver.observe(target);
}

let _trackSecondaryLogoObserver = null;

function trackSecondaryLogo() {
  const target = document.querySelector(".track-for-header-logo");

  if (!target) return;
  if (_trackSecondaryLogoObserver) _trackSecondaryLogoObserver.disconnect();

  _trackSecondaryLogoObserver = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        document.body.classList.add("secondary-logo--visible");
      } else {
        document.body.classList.remove("secondary-logo--visible");
      }
    },
    {
      root: null,
      threshold: 0.01,
    }
  );

  _trackSecondaryLogoObserver.observe(target);
}

let _headerStuckObserver = null;

function headerStuck() {
  const header = document.querySelector('.shopify-section-header-sticky');
  if (!header) return;

  if (_headerStuckObserver) _headerStuckObserver.disconnect();

  _headerStuckObserver = new IntersectionObserver(
    ([entry]) => {
      if (!entry.isIntersecting) {
        document.body.classList.add('header--stuck');
      } else {
        document.body.classList.remove('header--stuck');
      }
    },
    {
      root: null,
      threshold: 0,
      rootMargin: `0px 0px -${header.offsetHeight}px 0px`,
    }
  );

  _headerStuckObserver.observe(header);
}


function mobileLink() {
  const shopAllButtons = document.querySelectorAll("summary[data-location]");

  shopAllButtons.forEach((button) => {
    button.addEventListener("click", function (e) {
      if (window.innerWidth <= 768) {
        e.preventDefault();
        const location = button.getAttribute("data-location");
        if (location) {
          window.location.href = location;
        }
      }
    });
  });
}

let _headerStuckTimer;
window.addEventListener('resize', function () {
  clearTimeout(_headerStuckTimer);
  _headerStuckTimer = setTimeout(headerStuck, 150);
});

function pauseMarqueesOffScreen() {
  const marqueeEls = document.querySelectorAll('.sliding__track, .notice-contact__marquee-track');
  if (!marqueeEls.length) return;
  marqueeEls.forEach(function(el) {
    const section = el.closest('.shopify-section') || el.parentElement;
    if (!section || section.dataset.marqueeObserverBound) return;
    section.dataset.marqueeObserverBound = '1';
    const _mObs = new IntersectionObserver(function([entry]) {
      section.classList.toggle('marquee-section--paused', !entry.isIntersecting);
    }, { rootMargin: '200px' });
    _mObs.observe(section);
    section._marqueeObserver = _mObs;
  });
}

document.addEventListener('DOMContentLoaded', function () {
  // Core UI init — always run on every page
  footerInView();
  headerStuck();
  mobileLink();
  pauseMarqueesOffScreen();

  // Collection / search pages render the secondary logo tracker via custom-liquid
  if (document.querySelector('.track-for-header-logo')) trackSecondaryLogo();

  // Collection search filter — grid items cached once at init, debounced input
  var searchBar = document.getElementById('search--bar');
  if (searchBar) {
    var _gridItems = document.querySelectorAll('#product-grid .grid__item');
    var searchDebounceTimer;
    searchBar.addEventListener('input', function () {
      var val = this.value;
      clearTimeout(searchDebounceTimer);
      searchDebounceTimer = setTimeout(function () {
        var searchText = val.toLowerCase();
        _gridItems.forEach(function (item) {
          var title = (item.querySelector('.card__content .card__title') || {}).textContent || '';
          item.style.display = title.toLowerCase().includes(searchText) ? '' : 'none';
        });
      }, 200);
    });
  }
});

/* Notice perf: desktop-only collection hover-image hydrator.
   The card secondary (hover) image is invisible below 990px (CSS opacity:0,
   revealed only on hover inside a min-width:990px media query) yet was still
   downloaded on mobile. card-product.liquid now ships it as data-srcset/data-src
   with a 1x1 placeholder. Hydrate to the real image only on desktop:
   IntersectionObserver mirrors the prior loading="lazy" behavior (loads as a
   card nears the viewport, so desktop hover never flashes a placeholder), and a
   pointerover fallback catches any cards added later via AJAX filter/pagination.
   Below 990px this never runs, so mobile (90% of traffic) never fetches it. */
(function () {
  if (!window.matchMedia || !window.matchMedia('(min-width:990px)').matches) return;
  function hydrate(img) {
    if (!img || !img.dataset || !img.dataset.srcset) return;
    img.srcset = img.dataset.srcset;
    if (img.dataset.src) img.src = img.dataset.src;
    img.removeAttribute('data-srcset');
    img.removeAttribute('data-src');
  }
  function selectAll() {
    return document.querySelectorAll('.media--hover-effect img[data-srcset]');
  }
  if ('IntersectionObserver' in window) {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) { hydrate(en.target); io.unobserve(en.target); }
      });
    }, { rootMargin: '200px' });
    var observeAll = function () { selectAll().forEach(function (img) { io.observe(img); }); };
    observeAll();
    document.addEventListener('shopify:section:load', observeAll);
  }
  document.addEventListener('pointerover', function (e) {
    var t = e.target;
    var wrap = t && t.closest ? t.closest('.media--hover-effect') : null;
    if (wrap) hydrate(wrap.querySelector('img[data-srcset]'));
  }, { passive: true });
})();
